import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Nav from '../components/Nav';
import { getUserName, isDemoMode, isSuperAdmin } from '../auth/cognito';
import { getResults, getCompletedCount, getLatestScore, getAssessmentHistory, getOrgPlan, OrgPlan, AssessmentResult } from '../api/results';

export default function Dashboard() {
  const navigate = useNavigate();
  const name = getUserName();
  const [results, setResults] = useState<AssessmentResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<OrgPlan>('starter');
  const [orgStatus, setOrgStatus] = useState<string>('active');

  useEffect(() => {
    Promise.all([
      getResults(),
      getOrgPlan(),
    ]).then(([data, orgPlan]) => {
      setResults(data);
      setPlan(orgPlan.plan);
      setOrgStatus(orgPlan.status);
      setLoading(false);
    });
  }, []);

  // OrgAdmin whose org hasn't completed payment yet
  if (!loading && orgStatus === 'pending_payment' && !isSuperAdmin()) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#0a0a1a,#0d1b3e,#050d1f)', fontFamily: "'Segoe UI',system-ui,sans-serif", color: '#fff', paddingTop: 80 }}>
        <Nav />
        <div style={{ maxWidth: 520, margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 20 }}>💳</div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#fff', margin: '0 0 12px' }}>Payment required</h1>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, margin: '0 0 8px' }}>
            Your organisation's subscription hasn't been activated yet.
          </p>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', lineHeight: 1.7, margin: '0 0 32px' }}>
            Please complete payment through the admin portal at{' '}
            <a href="https://admin.privacy.education" style={{ color: '#818cf8' }}>admin.privacy.education</a>, or contact your organisation's billing contact.
          </p>
          <a
            href="https://admin.privacy.education"
            style={{ display: 'inline-block', padding: '12px 28px', borderRadius: 12, fontSize: 14, fontWeight: 600, background: 'rgba(99,102,241,0.8)', color: '#fff', textDecoration: 'none' }}
          >
            Go to Admin Portal
          </a>
        </div>
      </div>
    );
  }

  const demo = isDemoMode();
  const canAccessPbD = demo || plan === 'professional' || plan === 'enterprise';
  const canAccessBuilder = demo || plan === 'enterprise';

  const completedCount = getCompletedCount(results);
  const latestScore = getLatestScore(results);
  const history = getAssessmentHistory(results);

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#0a0a1a,#0d1b3e,#050d1f)', fontFamily: "'Segoe UI',system-ui,sans-serif", color: '#fff', paddingTop: 80 }}>
      <Nav />
      <div style={{ maxWidth: 1040, margin: '0 auto', padding: '32px 20px 64px' }}>

        {/* Greeting */}
        <h1 style={{ fontSize: 'clamp(22px,4vw,34px)', fontWeight: 700, letterSpacing: -1, marginBottom: 6 }}>
          Good to see you, <span style={{ color: '#818cf8' }}>{name || 'there'}</span>.
        </h1>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.35)', marginBottom: 36 }}>
          Your privacy assessment dashboard · privacy-education-enterprise
        </p>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 44 }}>
          {[
            { num: loading ? '…' : completedCount, label: 'Assessments completed' },
            { num: loading ? '…' : latestScore !== null ? latestScore : '—', label: 'Latest risk score' },
            { num: canAccessPbD ? '3' : '2', label: 'Modules available' },
          ].map((s, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 16, padding: '18px 22px' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#818cf8' }}>{s.num}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── FEATURED: Privacy by Design Zone ── */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700, color: '#10b981', marginBottom: 14 }}>
            ★ Featured Zone
          </div>
        </div>

        <div
          onClick={() => canAccessPbD && navigate('/privacy-by-design')}
          style={{
            background: canAccessPbD
              ? 'linear-gradient(135deg,rgba(16,185,129,0.1),rgba(5,150,105,0.06),rgba(99,102,241,0.08))'
              : 'rgba(255,255,255,0.03)',
            border: canAccessPbD ? '1.5px solid rgba(16,185,129,0.35)' : '1.5px solid rgba(255,255,255,0.08)',
            borderRadius: 24,
            padding: '36px 40px',
            marginBottom: 48,
            cursor: canAccessPbD ? 'pointer' : 'default',
            position: 'relative',
            overflow: 'hidden',
            transition: 'border-color 0.2s',
            opacity: canAccessPbD ? 1 : 0.6,
          }}
          onMouseEnter={e => { if (canAccessPbD) e.currentTarget.style.borderColor = 'rgba(16,185,129,0.65)'; }}
          onMouseLeave={e => { if (canAccessPbD) e.currentTarget.style.borderColor = 'rgba(16,185,129,0.35)'; }}
        >
          {/* Decorative glow */}
          <div style={{ position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'radial-gradient(circle,rgba(16,185,129,0.12),transparent 70%)', pointerEvents: 'none' }} />

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 280 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <span style={{ fontSize: 32 }}>🔒</span>
                <div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700, color: '#34d399', background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)', padding: '3px 10px', borderRadius: 100, marginBottom: 6 }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#34d399' }} />
                    Live · All Teams
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#fff', letterSpacing: -0.5 }}>Privacy by Design Zone</div>
                </div>
              </div>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.75, marginBottom: 24, maxWidth: 520 }}>
                Three integrated tools in one interactive zone — explore the 7 foundational PbD principles by audience, map threats against your system with guided mitigations, and use our flagship architecture builder to analyse your AWS stack for privacy risk.
              </p>

              {/* Three tools breakdown */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 28 }}>
                <div style={{ padding: '8px 14px', borderRadius: 10, background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#a5b4fc' }}>📖 7 PbD Principles</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>CXO + Engineering views</div>
                </div>
                <div style={{ padding: '8px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#fca5a5' }}>⚠️ Threat Modelling</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>8 threat vectors + mitigations</div>
                </div>
                {/* AWS Builder chip — gated to Enterprise */}
                {canAccessBuilder ? (
                  <div style={{ padding: '8px 14px', borderRadius: 10, background: 'linear-gradient(135deg,rgba(168,85,247,0.2),rgba(99,102,241,0.15))', border: '1.5px solid rgba(168,85,247,0.5)', position: 'relative' }}>
                    <div style={{ position: 'absolute', top: -7, right: 8, fontSize: 9, fontWeight: 800, color: '#fff', background: '#7c3aed', padding: '2px 7px', borderRadius: 10, letterSpacing: 1, textTransform: 'uppercase' }}>USP</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#d8b4fe' }}>🏗️ AWS Architecture Builder</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>Region-aware · Cross-border analysis</div>
                  </div>
                ) : (
                  <div style={{ padding: '8px 14px', borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', position: 'relative' }}>
                    <div style={{ position: 'absolute', top: -7, right: 8, fontSize: 9, fontWeight: 800, color: '#94a3b8', background: '#1e293b', padding: '2px 7px', borderRadius: 10, letterSpacing: 1, textTransform: 'uppercase', border: '1px solid rgba(255,255,255,0.1)' }}>Enterprise</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.35)' }}>🏗️ AWS Architecture Builder</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', marginTop: 2 }}>Upgrade to Enterprise to unlock</div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 20, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
                <span>⏱ 30 min</span>
                <span>📊 3 tools</span>
                <span>👥 All Teams</span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 180, alignItems: 'flex-end' }}>
              {canAccessPbD ? (
                <>
                  <button
                    style={{ background: 'linear-gradient(135deg,#10b981,#059669)', border: 'none', color: '#fff', padding: '14px 28px', borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                    onClick={e => { e.stopPropagation(); navigate('/privacy-by-design'); }}
                  >
                    Enter Zone →
                  </button>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', textAlign: 'right' }}>
                    {canAccessBuilder ? 'Architecture builder included' : 'Upgrade to Enterprise for full access'}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)', padding: '14px 28px', borderRadius: 12, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', textAlign: 'center' }}>
                    🔒 Locked
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', textAlign: 'right' }}>Professional plan required</div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── TWO SECTIONS ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>

          {/* ── LEFT: CXO & LEADERSHIP ── */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <div style={{ width: 3, height: 28, borderRadius: 2, background: 'linear-gradient(180deg,#c084fc,#7c3aed)' }} />
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#e9d5ff' }}>For Leadership & CXO</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 1 }}>Strategic decision-making · Incident response</div>
              </div>
            </div>

            {/* Tabletop — active */}
            <div
              onClick={() => navigate('/tabletop')}
              style={{ background: 'rgba(168,85,247,0.08)', border: '1.5px solid rgba(168,85,247,0.25)', borderRadius: 18, padding: '24px', marginBottom: 14, cursor: 'pointer', transition: 'border-color 0.2s' }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(168,85,247,0.55)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(168,85,247,0.25)')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#c084fc' }} />
                <div style={{ fontSize: 10, color: '#c084fc', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2 }}>Available</div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 26, flexShrink: 0 }}>🎯</span>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 6 }}>Privacy Tabletop Exercise</div>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.65, marginBottom: 14 }}>
                    Scenario-based incident response for leadership. Three real-world privacy crises — your decisions are scored and mapped to a risk report.
                  </p>
                  <div style={{ display: 'flex', gap: 14, fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 16 }}>
                    <span>⏱ 15 min</span><span>📊 3 scenarios</span>
                  </div>
                  <button style={{ background: 'linear-gradient(135deg,#a855f7,#7c3aed)', border: 'none', color: '#fff', padding: '9px 20px', borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    Begin →
                  </button>
                </div>
              </div>
            </div>

            {/* DPIA — locked */}
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 18, padding: '24px', opacity: 0.55 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(255,255,255,0.2)' }} />
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2 }}>Coming Q3 2025</div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 26, flexShrink: 0 }}>📋</span>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>DPIA Workshop</div>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', lineHeight: 1.65, marginBottom: 14 }}>
                    Guided Data Protection Impact Assessment for high-risk processing activities including AI systems and cross-border data flows.
                  </p>
                  <div style={{ display: 'flex', gap: 14, fontSize: 11, color: 'rgba(255,255,255,0.2)', marginBottom: 16 }}>
                    <span>⏱ 45 min</span><span>📊 6 modules</span>
                  </div>
                  <button disabled style={{ background: 'rgba(255,255,255,0.05)', border: 'none', color: 'rgba(255,255,255,0.2)', padding: '9px 20px', borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: 'not-allowed' }}>
                    Coming Q3 2025
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── RIGHT: ENGINEERING & TECHNICAL ── */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <div style={{ width: 3, height: 28, borderRadius: 2, background: 'linear-gradient(180deg,#a5b4fc,#4f46e5)' }} />
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#c7d2fe' }}>For Engineering & Technical</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 1 }}>Architecture · Data flows · Compliance</div>
              </div>
            </div>

            {/* Engineering Assessment — active */}
            <div
              onClick={() => navigate('/engineering')}
              style={{ background: 'rgba(99,102,241,0.08)', border: '1.5px solid rgba(99,102,241,0.25)', borderRadius: 18, padding: '24px', marginBottom: 14, cursor: 'pointer', transition: 'border-color 0.2s' }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(99,102,241,0.55)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(99,102,241,0.25)')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#a5b4fc' }} />
                <div style={{ fontSize: 10, color: '#a5b4fc', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2 }}>Available</div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 26, flexShrink: 0 }}>🏗️</span>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 6 }}>Privacy Engineering Assessment</div>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.65, marginBottom: 14 }}>
                    Technical deep-dive across data classification, consent management, cross-border transfers, and AI bias. Built for engineering leads and architects.
                  </p>
                  <div style={{ display: 'flex', gap: 14, fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 16 }}>
                    <span>⏱ 20 min</span><span>📊 4 modules</span>
                  </div>
                  <button style={{ background: 'linear-gradient(135deg,#6366f1,#4f46e5)', border: 'none', color: '#fff', padding: '9px 20px', borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    Begin →
                  </button>
                </div>
              </div>
            </div>

            {/* AI Governance — locked */}
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 18, padding: '24px', opacity: 0.55 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(255,255,255,0.2)' }} />
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2 }}>Coming Q4 2025</div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 26, flexShrink: 0 }}>🤖</span>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>AI Governance Audit</div>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', lineHeight: 1.65, marginBottom: 14 }}>
                    End-to-end AI system audit covering training data governance, model bias testing, algorithmic accountability, and EU AI Act compliance.
                  </p>
                  <div style={{ display: 'flex', gap: 14, fontSize: 11, color: 'rgba(255,255,255,0.2)', marginBottom: 16 }}>
                    <span>⏱ 60 min</span><span>📊 8 modules</span>
                  </div>
                  <button disabled style={{ background: 'rgba(255,255,255,0.05)', border: 'none', color: 'rgba(255,255,255,0.2)', padding: '9px 20px', borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: 'not-allowed' }}>
                    Coming Q4 2025
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Assessment History */}
        {!loading && history.length > 0 && (
          <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: 20, padding: 24, marginTop: 44 }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: '#818cf8', marginBottom: 16, fontWeight: 700 }}>Assessment History</div>
            {history.map((h, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < history.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 2 }}>{h.type}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>{h.date}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: h.riskColor }}>{h.score}</div>
                  <div style={{ background: `${h.riskColor}20`, border: `1px solid ${h.riskColor}40`, color: h.riskColor, padding: '3px 10px', borderRadius: 100, fontSize: 11, fontWeight: 700 }}>{h.risk} Risk</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Book a session */}
        <div style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 16, padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginTop: 32 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Need a guided session?</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>Book a 1:1 debrief with Omer to walk through your results and build a remediation roadmap.</div>
          </div>
          <a href="mailto:omer@privacy.education?subject=Debrief%20Request" style={{ background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.35)', color: '#a5b4fc', padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', whiteSpace: 'nowrap' }}>
            Book a session →
          </a>
        </div>

      </div>
    </div>
  );
}
